import { getTextStylesFromBrandStyle } from 'modules/BrandDefinition';
import { useCallback, useEffect, useRef } from 'react';
import * as Constants from 'const';
import * as Models from 'models';
import { TextEditorHook } from './TextEditorHook';
import useStyles from './useStyles';

type HookProps = {
  activeLayer: Constants.Layer;
  relation: Models.LayeredRegularRelationMap<Models.TextRelationStyles>;
  isAutoFitContent: boolean;
  toggleAutoFitContent: () => void;
  colors: Models.BrandColorsList;
  fonts: Models.BrandFontsList;
  brandStyles: Models.BrandStylesMap;
};

type Hook = {
  setBrandStyle: (style: Models.BrandStyleMap) => void;
};

export default function useBrandStyle(
  props: HookProps,
  editorHook: TextEditorHook,
  stylesHook: ReturnType<typeof useStyles>,
): Hook {
  const {
    relation, activeLayer,
    colors, fonts, brandStyles,
  } = props;
  const prevBrandStylesRef = useRef(brandStyles);

  const { applyBrandStyleValues: editorApplyBrandStyleValues } = editorHook;
  const { applyBrandStyle: stylesApplyBrandStyle } = stylesHook;

  const setBrandStyle = useCallback((brandStyle: Models.BrandStyleMap): void => {
    const textStyles = getTextStylesFromBrandStyle(brandStyle, colors, fonts);

    editorApplyBrandStyleValues(textStyles);

    stylesApplyBrandStyle(brandStyle, textStyles);
  }, [
    colors, fonts,
    editorApplyBrandStyleValues,
    stylesApplyBrandStyle,
  ]);

  useEffect(() => {
    const prevBrandStyles = prevBrandStylesRef.current;
    // skip run on mount
    if (prevBrandStyles === brandStyles) {
      return;
    }

    // we intentionally use relation.styles instead of stylesHook.styles
    const styles = relation.getIn(['styles', activeLayer]);
    const brandStyleId = styles?.get('brandStyleId');
    const brandStyleChanged = styles?.get('brandStyleChanged');

    if (!brandStyles
      || !brandStyleId
      || brandStyleChanged
      || brandStyles.equals(prevBrandStyles)
    ) {
      return;
    }

    // ??? not trivial approach
    setBrandStyle(brandStyles.get(brandStyleId));
    stylesHook.ensureStylesToBeKept();
  }, [brandStyles]);

  useEffect(() => {
    prevBrandStylesRef.current = brandStyles;
  });

  return { setBrandStyle };
}
